<template>
  <div class="wm" :style="style" aria-hidden="true"></div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  text: { type: String, default: '' },
  /** 字号（真实屏幕像素）。本层在 AutoFit 之外，不吃驾驶舱的设计稿缩放 */
  size: { type: Number, default: 40 },
  opacity: { type: Number, default: 0.18 },
  /** 平铺单元在文字之外多留的空白，单位是字号倍数 —— 调大就变稀 */
  gapX: { type: Number, default: 2.2 },
  gapY: { type: Number, default: 3 },
  /** 整行文字的抬升角（度） */
  angle: { type: Number, default: 22 },
  /** 字形自身的前倾角（度）：中文字体没有真斜体，只能用 shear 硬造出斜体观感 */
  shear: { type: Number, default: 12 },
  /** rgb 三元组，配合 opacity 合成 rgba */
  color: { type: String, default: '255, 255, 255' },
})

const rad = (d) => (d * Math.PI) / 180
const tile = ref({ url: '', w: 0, h: 0 })

/**
 * 一个平铺单元 = canvas 上「中心一份 + 四角各一份」的同一行斜字。
 * 四角那四份会被画布裁掉一半，但相邻单元补回的正好是缺的那半，所以平铺无缝；
 * 中心那份相对四角错开半个单元，铺出来是砖缝式排布而不是死对齐的方阵。
 */
function render() {
  const text = props.text.trim()
  if (!text) {
    tile.value = { url: '', w: 0, h: 0 }
    return
  }

  // 单元尺寸必须容得下旋转+斜切后的水平包围盒，否则字被切在单元边界上
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
  const probe = document.createElement('canvas').getContext('2d')
  const font = `${props.size}px 'Microsoft YaHei', 'PingFang SC', sans-serif`
  probe.font = font
  const shearTan = Math.tan(rad(props.shear))
  const boxW = probe.measureText(text).width + props.size * shearTan
  const boxH = props.size * 1.35
  const ca = Math.cos(rad(props.angle))
  const sa = Math.sin(rad(props.angle))
  const w = Math.ceil(boxW * ca + boxH * sa + props.size * props.gapX)
  const h = Math.ceil(boxW * sa + boxH * ca + props.size * props.gapY)

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)
  ctx.font = font
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = `rgba(${props.color}, ${props.opacity})`
  ;[[w / 2, h / 2], [0, 0], [w, 0], [0, h], [w, h]].forEach(([x, y]) => {
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(-rad(props.angle))
    // 先转行、再在行自身坐标系里斜切：基线上方（y<0）右移，就是西文字体那种前倾
    ctx.transform(1, 0, -shearTan, 1, 0, 0)
    ctx.fillText(text, 0, 0)
    ctx.restore()
  })

  tile.value = { url: canvas.toDataURL('image/png'), w, h }
}

watch(() => [props.text, props.size, props.opacity, props.angle, props.shear, props.gapX, props.gapY, props.color], render)
render()

const style = computed(() => {
  const t = tile.value
  if (!t.url) return {}
  // base64 里不含引号，加引号包起来最稳
  return { backgroundImage: `url("${t.url}")`, backgroundSize: `${t.w}px ${t.h}px` }
})
</script>

<style scoped>
.wm {
  position: absolute;
  inset: 0;
  /* 压住 AutoFit（100）和校准面板（200）：水印要盖的是整屏画面，包括面板本身 */
  z-index: 300;
  pointer-events: none;
  background-repeat: repeat;
  background-position: 0 0;
}
</style>
